import React, { useState, useMemo } from 'react';
import { TimberProject, Milestone, ProjectTask } from '../../types';
import { Mail, Phone, Check } from 'lucide-react';
import './ClientPortal.css';

interface ClientPortalProps {
  project: TimberProject;
  milestones: Milestone[];
  tasks: ProjectTask[];
}

const STATUS_LABELS: Record<string, string> = {
  planning: 'In Planning',
  design: 'Design Phase',
  production: 'In Production',
  installation: 'Installation',
  completed: 'Completed',
  'on-hold': 'On Hold',
};

const STATUS_COLORS: Record<string, string> = {
  planning: '#9F7AEA',
  design: '#4299E1',
  production: '#ED8936',
  installation: '#38B2AC',
  completed: '#48BB78',
  'on-hold': '#A0AEC0',
};

export const ClientPortal: React.FC<ClientPortalProps> = ({
  project,
  milestones,
  tasks,
}) => {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');

  // Calculate overall progress
  const progress = useMemo(() => {
    const completedMilestones = milestones.filter(m => m.status === 'completed').length;
    const totalMilestones = milestones.length;
    
    if (totalMilestones === 0) return 0;
    return Math.round((completedMilestones / totalMilestones) * 100);
  }, [milestones]);

  // Group tasks by status for summary
  const taskSummary = useMemo(() => {
    return {
      completed: tasks.filter(t => t.status === 'completed').length,
      inProgress: tasks.filter(t => t.status === 'in-progress').length,
      pending: tasks.filter(t => t.status === 'pending' || t.status === 'review').length,
      total: tasks.length,
    };
  }, [tasks]);

  // Sort milestones by date
  const sortedMilestones = useMemo(() => {
    return [...milestones].sort((a, b) => 
      new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime()
    );
  }, [milestones]);

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysRemaining = (): number => {
    const endDate = new Date(project.targetEndDate);
    const today = new Date();
    return Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const daysRemaining = getDaysRemaining();

  return (
    <div className="client-portal">
      {/* Header */}
      <div className="portal-header">
        <div className="portal-branding">
          <img 
            src="/logo.png" 
            alt="LC Timber Frames" 
            className="portal-logo"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <span className="portal-title">Project Portal</span>
        </div>
        <div className="portal-user">
          <span className="user-name">{project.clientName}</span>
          <span className="project-name">{project.name}</span>
        </div>
      </div>

      {/* Project Status Overview */}
      <div className="portal-status-card">
        <div className="status-main">
          <div 
            className="status-indicator"
            style={{ backgroundColor: STATUS_COLORS[project.status] }}
          />
          <div className="status-info">
            <span className="status-label">{STATUS_LABELS[project.status]}</span>
            <span className="status-detail">
              {daysRemaining > 0 
                ? `${daysRemaining} days until completion`
                : daysRemaining === 0 
                  ? 'Due today' 
                  : `${Math.abs(daysRemaining)} days past target`}
            </span>
          </div>
        </div>
        <div className="status-progress">
          <div className="progress-ring">
            <svg viewBox="0 0 36 36" className="progress-circle">
              <path
                className="progress-bg"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="progress-fill"
                strokeDasharray={`${progress}, 100`}
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                style={{ stroke: STATUS_COLORS[project.status] }}
              />
            </svg>
            <span className="progress-text">{progress}%</span>
          </div>
          <span className="progress-label">Complete</span>
        </div>
      </div>

      {/* Collapsible Sections */}
      <div className="portal-sections">
        {/* Overview Section */}
        <div className={`portal-section ${expandedSection === 'overview' ? 'expanded' : ''}`}>
          <button 
            className="section-header"
            onClick={() => setExpandedSection(expandedSection === 'overview' ? null : 'overview')}
          >
            <span className="section-title">Project Overview</span>
            <span className="section-toggle">{expandedSection === 'overview' ? '−' : '+'}</span>
          </button>
          {expandedSection === 'overview' && (
            <div className="section-content">
              <div className="overview-grid">
                <div className="overview-item">
                  <span className="overview-label">Project Type</span>
                  <span className="overview-value">{project.projectType}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Location</span>
                  <span className="overview-value">{project.location}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Start Date</span>
                  <span className="overview-value">{formatDate(project.startDate)}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Target Completion</span>
                  <span className="overview-value">{formatDate(project.targetEndDate)}</span>
                </div>
              </div>
              {project.description && (
                <div className="overview-description">
                  <span className="overview-label">Description</span>
                  <p>{project.description}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Timeline Section */}
        <div className={`portal-section ${expandedSection === 'timeline' ? 'expanded' : ''}`}>
          <button 
            className="section-header"
            onClick={() => setExpandedSection(expandedSection === 'timeline' ? null : 'timeline')}
          >
            <span className="section-title">Milestone Timeline</span>
            <span className="section-toggle">{expandedSection === 'timeline' ? '−' : '+'}</span>
          </button>
          {expandedSection === 'timeline' && (
            <div className="section-content">
              {sortedMilestones.length === 0 ? (
                <p className="empty-message">No milestones have been set yet.</p>
              ) : (
                <div className="milestone-timeline">
                  {sortedMilestones.map((milestone, index) => {
                    const isCompleted = milestone.status === 'completed';
                    const isPast = new Date(milestone.targetDate) < new Date() && !isCompleted;
                    
                    return (
                      <div 
                        key={milestone.id}
                        className={`milestone-item ${isCompleted ? 'completed' : ''} ${isPast ? 'overdue' : ''}`}
                      >
                        <div className="milestone-connector">
                          <div className="milestone-dot">
                            {isCompleted && <span className="check"><Check size={12} /></span>}
                          </div>
                          {index < sortedMilestones.length - 1 && (
                            <div className={`milestone-line ${isCompleted ? 'completed' : ''}`} />
                          )}
                        </div>
                        <div className="milestone-content">
                          <span className="milestone-title">{milestone.title}</span>
                          <span className="milestone-date">
                            {formatDate(milestone.targetDate)}
                            {isCompleted && milestone.completedDate && (
                              <> · Completed {formatDate(milestone.completedDate)}</>
                            )}
                          </span>
                          {milestone.description && (
                            <p className="milestone-desc">{milestone.description}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Progress Section */}
        <div className={`portal-section ${expandedSection === 'progress' ? 'expanded' : ''}`}>
          <button 
            className="section-header"
            onClick={() => setExpandedSection(expandedSection === 'progress' ? null : 'progress')}
          >
            <span className="section-title">Work Progress</span>
            <span className="section-toggle">{expandedSection === 'progress' ? '−' : '+'}</span>
          </button>
          {expandedSection === 'progress' && (
            <div className="section-content">
              <div className="progress-summary">
                <div className="progress-stat">
                  <span className="stat-number">{taskSummary.completed}</span>
                  <span className="stat-label">Completed</span>
                </div>
                <div className="progress-stat">
                  <span className="stat-number">{taskSummary.inProgress}</span>
                  <span className="stat-label">In Progress</span>
                </div>
                <div className="progress-stat">
                  <span className="stat-number">{taskSummary.pending}</span>
                  <span className="stat-label">Remaining</span>
                </div>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div 
                    className="progress-bar-fill completed"
                    style={{ width: `${(taskSummary.completed / Math.max(taskSummary.total, 1)) * 100}%` }}
                  />
                  <div 
                    className="progress-bar-fill in-progress"
                    style={{ width: `${(taskSummary.inProgress / Math.max(taskSummary.total, 1)) * 100}%` }}
                  />
                </div>
                <span className="progress-bar-label">
                  {taskSummary.total > 0 
                    ? `${Math.round((taskSummary.completed / taskSummary.total) * 100)}% of tasks completed`
                    : 'No tasks yet'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Contact Section */}
        <div className={`portal-section ${expandedSection === 'contact' ? 'expanded' : ''}`}>
          <button 
            className="section-header"
            onClick={() => setExpandedSection(expandedSection === 'contact' ? null : 'contact')}
          >
            <span className="section-title">Contact Your Team</span>
            <span className="section-toggle">{expandedSection === 'contact' ? '−' : '+'}</span>
          </button>
          {expandedSection === 'contact' && (
            <div className="section-content">
              <div className="contact-card">
                <div className="contact-info">
                  <span className="contact-label">Project Manager</span>
                  <span className="contact-value">{project.projectManager || 'To be assigned'}</span>
                </div>
                <div className="contact-actions">
                  <a href="mailto:info@lctimberframes.com" className="contact-btn">
                    <span className="contact-icon"><Mail size={16} /></span>
                    Email Us
                  </a>
                  <a href="tel:+1-555-123-4567" className="contact-btn">
                    <span className="contact-icon"><Phone size={16} /></span>
                    Call Us
                  </a>
                </div>
              </div>
              <p className="contact-note">
                Have questions about your project? Our team is here to help. 
                Reach out anytime for updates or assistance.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="portal-footer">
        <p>© {new Date().getFullYear()} LC Timber Frames. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ClientPortal;
